import * as premiumApi from "../../api/premiumApi";
import * as companyApi from "../../api/companyApi";
import swal from "sweetalert";
import { format, parseISO, isBefore } from "date-fns";
import { nb } from "date-fns/locale";

const differenceTreshold = 0.05;

// Functions for saving
export async function savePremium(premium, mode = "new") {
  verifyPremiumFields(premium)
  try {

    let premiumLastYear;

    if (premium.membership === "TFF") {
      premiumLastYear = await premiumApi.getLastYearsPremium(
        premium.orgMemId,
        premium.prizeYear - 1,
        premium.reportedCodeTff
        // TODO
        // Also compare against values of 0
      );
    } else {
      premiumLastYear = await premiumApi.getLastYearsPremium(
        premium.orgMemId,
        premium.prizeYear - 1
      );
    }

    if (premiumLastYear) {
      const isBeyondTreshold =
        premium.amount > premiumLastYear.amount * (1 + differenceTreshold) ||
        premium.amount < premiumLastYear.amount * (1 - differenceTreshold);

      if (isBeyondTreshold) {
        const willSave = await swal({
          title: "Er du sikker?",
          text: `Premie for ${premiumLastYear.prizeYear
            } er ${premiumLastYear.amount.toLocaleString(
              "de-DE"
            )}. Differansen mellom de to summene er over 5%. `,
          icon: "warning",
          buttons: ["Avbryt", "Lagre"],
        });
        if (!willSave) {
          console.log("User canceled the save operation.");
          return null;
        }
      }
    }

    if (mode === "new") {
      return await saveNewPremium(premium);
    } else if (mode === "update") {
      return await updatePremium(premium);
    } else {
      throw new Error(`Invalid mode: ${mode}. Use "new" or "update".`);
    }
  } catch (error) {
    console.log(" Error in saving premium", error);
    return null;
  }
};

async function saveNewPremium(premium) {
  try {
    const response = await premiumApi.newPremium(premium, true);
    if (response.success) {
      swal("Premie er lagret!", {
        icon: "success",
      });
      const prizeId = response.data.prizeId;
      const ownerships = await premiumApi.getOwnerships(prizeId) || [];

      const savedPremium = {
        ownerships,
        prizeId
      };
      return (savedPremium);
    } else {
      swal({
        title: "Feil ved lagring",
        text: response.message || "Det skjedde noe feil...",
        icon: "error",
      });
    }
  } catch (error) {
    console.error("Error in saving premium:", error);
    return null;
  }
}

// TODO
// Accept returned object to update frontend
async function updatePremium(premium) {
  try {
    const response = await premiumApi.updatePremium(premium, true);
    if (response.success) {
      swal("Premie er oppdatert!", {
        icon: "success",
      });
      return true;
    } else {
      swal({
        title: "Feil ved oppdatering",
        text: response.message || "Det skjedde noe feil...",
        icon: "error",
      });
    }
  } catch (error) {
    console.error("Error in updating premium:", error);
  }
}

export async function transferPremiumToCodeAndHandle(prizeId, prizeYear, targetTffCode) {
  if (!prizeId || !targetTffCode) {
    console.warn("Mangler prizeId eller targetTffCode.");
    swal({
      title: "Feil",
      text: "Mangler premie-ID eller TFF-kode.",
      icon: "error",
    });
    return false;
  }

  const confirmation = await swal({
    title: "Bekreft overføring av premie",
    text: `Er du sikker på at du vil overføre denne premien til TFF-kode ${targetTffCode}? Denne handlingen kan ikke angres.`,
    icon: "warning",
    buttons: ["Avbryt", "Ja, overfør"],
    dangerMode: true,
  });


  if (!confirmation) {
    console.log("Overføring avbrutt av bruker.");
    return false;
  }


  try {
    const response = await premiumApi.transferPremiumToTffCode({
      prizeId,
      prizeYear,
      targetTffCode: parseInt(targetTffCode, 10),
    });

    if (response?.success) {
      swal("Overføring fullført!", {
        icon: "success",
      });
      return true;
    } else {
      swal({
        title: "Feil ved overføring",
        text: response?.message || "Det skjedde noe feil under overføringen.",
        icon: "error",
      });
      return false;
    }
  } catch (error) {
    console.error(`Feil under overføring av premie ${prizeId}:`, error);
    swal({
      title: "Teknisk feil",
      text: "Det oppstod en teknisk feil under overføringen.",
      icon: "error",
    });
    return false;
  }
}

function verifyPremiumFields(premium) {
  const errors = {};

  if (!premium.orgMemId || typeof premium.orgMemId !== "number") {
    errors.orgMemId = "orgMemId is required and must be a number.";
  }

  if (!premium.prizeYear || !Number.isInteger(premium.prizeYear)) {
    errors.prizeYear = "prizeYear is required and must be an integer.";
  } else if (premium.prizeYear < 1900 || premium.prizeYear > new Date().getFullYear()) {
    errors.prizeYear = `prizeYear must be between 1900 and ${new Date().getFullYear()}.`;
  }

  if ((!premium.amount || typeof premium.amount !== "number" || premium.amount < 0) && premium.amount !== 0) {
    errors.amount = "amount is required, must be a number, and must not be less than 0.";
  }

  // Return errors or true if valid
  return Object.keys(errors).length === 0 ? true : errors;
}

export function formIsValid(premium, mode = "new") {
  var error = {};
  var newAmount = -1;

  if (mode === "update" || mode === "new") {
    if (mode === "new") {
      if (!premium.orgMemId) error.orgMemId = "Medlem er påkrevd";
      if (premium.prizeYear) {
        if (!Number.isInteger(Number(premium.prizeYear)))
          error.prizeYear = "Ugyldig år";
        if (
          premium.prizeYear < 1900 ||
          premium.prizeYear > parseInt(new Date().getFullYear(), 10)
        )
          error.prizeYear = `År må være mellom 1900 - ${parseInt(
            new Date().getFullYear(),
            10
          )}`;
      }
      if (!premium.prizeYear) error.prizeYear = "År er påkrevd";
    }
  
    const parsedAmount = parseFloat(String(premium.amount).replace(/[. ]/g, ""));

    if (parsedAmount !== 0 && !premium.amount) {
      error.amount = "Premie er påkrevd";
      console.log("Premie er påkrevd")
    } else {
      if (parsedAmount < 0) {
        error.amount = "Premie kan ikke være negativ";
        console.log("Premie kan ikke være negativ")
      } else if (parsedAmount >= 10000000000) {
        error.amount = "Premieverdi for høy"
        console.log("Premie er for høy")
      } else {
        newAmount = parsedAmount;
      }
    }

  } else {
    throw new Error(`Invalid mode: ${mode}. Use "new" or "update".`);
  }

  var valid = Object.keys(error).length === 0;
  if (valid && newAmount < 0) {
    valid = false;
    error.amount = "Feil i validering";
  }
  return { valid, error, newAmount };
};


// Functions for retrieving last premium
export async function fetchCompany(companyId) {
  try {
    return await companyApi.getCompany(companyId);
  } catch (error) {
    console.error("Error fetching company data", error);
    return null;
  }
}

export async function fetchOwnerships(prizeId) {
  try {
    return await premiumApi.getOwnerships(prizeId) || [];
  } catch (error) {
    console.error("Error fetching ownerships", error);
    return [];
  }
}

export async function fetchOrgMemberId(membershipType, companyId) {
  try {
    const members = await companyApi.getCompaniesByOrg(membershipType, companyId);
    return members && members.length > 0 ? members[0].orgMemId : null;
  } catch (error) {
    console.error("Error fetching organization member ID", error);
    return null;
  }
}

export async function fetchMembersByOrg(membership, companyId) {
  try {
    const members = await companyApi.getCompaniesByOrg(membership, companyId);
    if (members) {
      members.sort((a, b) => (a.companyName < b.companyName ? -1 : 1));
    }
    return members;
  } catch (error) {
    console.error("Error fetching members by org", error);
    return [];
  }
}

export function setDefaultPremium(company) {
  return {
    reportedByCompanyName: company ? company.companyName : "Unknown",
    prizeYear: -1,
    amount: -1,
    orgMemId: null,
  };
}

// Might have introduced some redundancy here. Will look to fix later, but works for now
export async function fetchExistingPremium(apiType, prizeYear, companyId, reportedCodeTff = undefined) {
  try {

    const premiums = await premiumApi.getPremiums(apiType, prizeYear, companyId);

    if (!premiums || premiums.length === 0) {
      return { existingPremium: false };
    }

    premiums.sort((a, b) => b.prizeYear - a.prizeYear);

    const relevantYear = premiums[0].prizeYear;
    const filteredPremiums = premiums.filter(p => p.prizeYear === relevantYear);

    let selectedPremium = null;

    if (apiType === "TFF") {
      const codeCount = {};
      filteredPremiums.forEach(p => {
        const code = p.codeTFF;
        codeCount[code] = (codeCount[code] || 0) + 1;
      });
      const duplicates = Object.entries(codeCount).filter(([code, count]) => count > 1);
      if (duplicates.length > 0) {
        console.warn("Duplicate TFF codes found:", duplicates.map(([code]) => code));
      }

      if (reportedCodeTff !== undefined) {
        selectedPremium = filteredPremiums.find(p => p.codeTFF === Number(reportedCodeTff)) || null;
      } else {
        console.warn("TFF premium fetch called without reportedCodeTff. No premium selected.");
      }

    } else {
      if (filteredPremiums.length > 1) {
        console.warn(`YFF: Multiple premiums found for year ${prizeYear}. Expected only one.`);
      }

      selectedPremium = filteredPremiums[0];
    }

    const ownerships = selectedPremium
      ? await fetchOwnerships(selectedPremium.prizeId)
      : [];
    return {
      existingPremium: true,
      prizeId: selectedPremium?.prizeId,
      existingAmount: selectedPremium?.amount,
      relevantYear,
      ownerships,
      allPremiums: filteredPremiums,
    };

  } catch (error) {
    console.error("Error in fetchExistingPremium in Utils:", error);
  }
}

// Functions for admins editing ownership
export function toggleDialog(dialogType, isOpen, data = null) {
  const newState = {
    [dialogType]: isOpen,
  };
  if (data) {
    newState[dialogType === "openEdit" ? "ownership" : "newOwnership"] = data;
  }
  return newState;
}

export function handleInputChange(field, value, ownershipType = "newOwnership", errorField = null, currentState) {
  const updatedOwnership = { ...currentState[ownershipType], [field]: value };
  const updatedError = errorField ? { ...currentState[errorField], [field]: "" } : {};

  return {
    [ownershipType]: updatedOwnership, ...(errorField && { [errorField]: updatedError }),
  };
}


export async function handleSaveDialog(ownershipType = "newOwnership", currentState) {
  const { isValid, errorNew, errorEdit } = ownershipType === "newOwnership"
    ? formIsValidNew(currentState.newOwnership)
    : formIsValidEdit(currentState.ownership);

  if (!isValid) {
    return {
      success: false,
      ...(ownershipType === "newOwnership" ? { errorNew } : { errorEdit }),
    };
  }

  const dialogKey = ownershipType === "newOwnership" ? "openNew" : "openEdit";
  const ownershipData = { ...currentState[ownershipType] };

  if (ownershipData.fromDate) ownershipData.fromDate = format(parseISO(ownershipData.fromDate), "yyyy-MM-dd", { locale: nb });
  if (ownershipData.toDate) ownershipData.toDate = format(parseISO(ownershipData.toDate), "yyyy-MM-dd", { locale: nb });

  const { membership, premium } = currentState;
  const apiCall = ownershipType === "newOwnership" ? premiumApi.newOwnership : premiumApi.updateOwnership;

  const response = await apiCall(
    membership,
    ownershipType === "newOwnership" ? premium.prizeId : ownershipData.prizeOwnershipId,
    ownershipData
  );

  if (response) {
    const updatedOwnerships = await fetchOwnerships(premium.prizeId);
    return {
      success: true,
      fetchOwnerships: updatedOwnerships,
      ...(ownershipType === "newOwnership" && { newOwnership: {} }),
      [dialogKey]: false,
    };
  } else {
    return { success: false };
  }
}

function formIsValidNew(newOwnership) {
  const errorNew = {};
  if (!newOwnership.ownedByOrgMemId) errorNew.ownedByOrgMemId = "error";
  if (!newOwnership.fromDate) errorNew.fromDate = "error";
  if (newOwnership.toDate && newOwnership.toDate < newOwnership.fromDate) {
    errorNew.toDate = "Til dato må være senere enn fra dato";
  }
  return { isValid: Object.keys(errorNew).length === 0, errorNew };
}

function formIsValidEdit(ownership) {
  const errorEdit = {};

  if (ownership.fromDate) {
    ownership.fromDate = format(parseISO(ownership.fromDate), "yyyy-MM-dd", { locale: nb });
  }
  if (ownership.toDate) {
    ownership.toDate = format(parseISO(ownership.toDate), "yyyy-MM-dd", { locale: nb });
  }
  if (ownership.toDate && isBefore(parseISO(ownership.toDate), parseISO(ownership.fromDate))) {
    errorEdit.toDate = "Til dato må være senere enn fra dato";
  }

  return { isValid: Object.keys(errorEdit).length === 0, errorEdit };
}
